import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarDays, MapPin, Users, ArrowLeft, Clock, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import CountdownTimer from '@/components/CountdownTimer';
import EventPoster from '@/components/EventPoster';
import { getEvent, createCheckoutSession, registerAttendee } from '@/lib/api';
import { formatDate, formatTime, formatPrice } from '@/lib/utils';
import { toast } from 'sonner';

export default function EventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Format phone number as user types
  const formatPhoneUS = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const formatPhoneSA = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length === 0) return '';
    if (digits.length <= 2) return `+${digits}`;
    if (digits.length <= 4) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 7) return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4)}`;
    return `+${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value;
    const isUS = (event?.location || '').toLowerCase().includes('texas') || 
                 (event?.location || '').toLowerCase().includes('dallas') || 
                 (event?.location || '').toLowerCase().includes('usa') ||
                 (event?.location || '').toLowerCase().includes('united states');
    const formatted = isUS ? formatPhoneUS(raw) : formatPhoneSA(raw);
    setFormData({...formData, phone: formatted});
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await getEvent(id);
        setEvent(res.data.event);
      } catch (e) {
        console.error('Failed to fetch event:', e);
        toast.error('Event not found');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all fields');
      return;
    }
    setSubmitting(true);
    try {
      // Fire Meta Pixel InitiateCheckout and CompleteRegistration events
      if (window.fbq) {
        window.fbq('track', 'InitiateCheckout');
        window.fbq('track', 'CompleteRegistration');
      }

      // Register attendee
      const regRes = await registerAttendee({
        event_id: id,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
      });

      const { payment_link, ticket_number } = regRes.data;

      if (isFreeEvent) {
        // Free event: redirect to WhatsApp to claim ticket
        toast.success('Registered! Redirecting to WhatsApp to claim your ticket...');
        const whatsappMsg = encodeURIComponent(
          `Hi, I registered for ${event.title}.\n\nName: ${formData.firstName} ${formData.lastName}\nTicket: ${ticket_number}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nPlease send me my ticket.`
        );
        setTimeout(() => {
          window.location.href = `https://wa.me/18623006513?text=${whatsappMsg}`;
        }, 1500);
      } else {
        // Paid event: redirect to Stripe
        toast.success('Redirecting to payment...');

        // Fire Meta Pixel AddPaymentInfo event
        if (window.fbq) {
          window.fbq('track', 'AddPaymentInfo');
        }

        if (payment_link) {
          setTimeout(() => {
            window.location.href = payment_link;
          }, 1000);
        } else if (event.payment_link) {
          setTimeout(() => {
            window.location.href = event.payment_link;
          }, 1000);
        } else {
          const originUrl = window.location.origin;
          const res = await createCheckoutSession({
            event_id: id,
            attendee_name: `${formData.firstName} ${formData.lastName}`,
            attendee_email: formData.email,
            attendee_phone: formData.phone,
            origin_url: originUrl,
          });
          if (res.data.url) {
            window.location.href = res.data.url;
          }
        }
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-2xl px-6">
          <div className="h-80 bg-gray-100" />
          <div className="h-8 bg-gray-100 rounded w-3/4" />
          <div className="h-4 bg-gray-100 rounded w-1/2" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-gray-900">Event Not Found</h2>
          <Link to="/revival-night">
            <Button className="mt-4 rounded-sm bg-[#1a2e4a] text-white">Back to Events</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isSoldOut = event.tickets_available === 0;
  const isExpired = new Date(event.start_at) < new Date();
  const isUSEvent = (event.location || '').toLowerCase().includes('texas') || 
                    (event.location || '').toLowerCase().includes('dallas') || 
                    (event.location || '').toLowerCase().includes('usa') ||
                    (event.location || '').toLowerCase().includes('united states');
  const isFreeEvent = !event.price || event.price === 0;
  const isBusinessSummitEvent = (event.title || '').toLowerCase().includes('business summit');
  const isMultiDayEvent = !!event.end_at;
  const isComingSoon = !event.payment_link && (!event.price || event.price === 0) && event.location === 'To be announced';

  const getCategoryPath = (category) => {
    const map = {
      'Revival Night': '/revival-night',
      'Prophetic School': '/prophetic-school',
      'Mentorship': '/mentorship',
    };
    return map[category] || '/revival-night';
  };
  const backPath = getCategoryPath(event.category);

  return (
    <div className="min-h-screen bg-white">
      {/* Back link */}
      <div className="mx-auto w-full max-w-5xl px-6 sm:px-10 pt-8">
        <Link to={backPath} className="inline-flex items-center text-sm text-gray-400 hover:text-gray-700 font-body transition-colors">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to {event.category}
        </Link>
      </div>

      {/* Editorial layout: centered image + content */}
      <div className="mx-auto w-full max-w-5xl px-6 sm:px-10 pt-8 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Left: Content */}
          <div className="lg:col-span-3">
            {/* Category */}
            <p className="text-[11px] uppercase tracking-[0.15em] text-gray-400 font-body font-medium">
              {event.category}
            </p>

            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-gray-900 mt-2 leading-tight">
              {event.title}
            </h1>

            {/* Meta */}
            <div className="mt-5 flex flex-wrap items-center gap-4 text-[13px] text-gray-500 font-body">
              <span className="flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4" />
                {formatDate(event.start_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Clock className="h-4 w-4" />
                {`${isBusinessSummitEvent ? '11:00 AM & 6:00 PM' : isMultiDayEvent ? 'April 23 — 25, 2026' : formatTime(event.start_at)}`}
              </span>
              {event.location && event.location !== 'To be announced' && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4" />
                  {event.location}
                </span>
              )}
            </div>

            {/* Event Poster / Image */}
            <div className="mt-8">
              <EventPoster eventTitle={event.title} />
              {!event.title?.toLowerCase().includes('johannesburg') && 
               !event.title?.toLowerCase().includes('south africa') && 
               !event.title?.toLowerCase().includes('dallas') && (
                <img
                  src={event.hero_image_url || ''}
                  alt={event.title}
                  className="w-full object-contain bg-[#f5f2ec]"
                />
              )}
            </div>

            <Separator className="my-8 bg-gray-100" />

            {/* Event Schedule - conditional based on event type */}
            {isBusinessSummitEvent ? (
              <div>
                <h3 className="font-heading text-xl font-medium text-gray-900 mb-5">Event Schedule</h3>
                <div className="space-y-4">
                  <div className="flex border border-gray-100 overflow-hidden" data-testid="event-schedule-summit">
                    <div className="bg-[#1a2332] text-white px-5 py-5 flex flex-col items-center justify-center min-w-[90px]">
                      <span className="font-heading text-2xl font-medium">11</span>
                      <span className="font-body text-[10px] uppercase tracking-wider text-white/70">AM</span>
                    </div>
                    <div className="px-5 py-4 flex-1">
                      <h4 className="font-heading text-lg font-medium text-gray-900">Business Summit</h4>
                      <p className="text-[13px] text-gray-500 font-body mt-1">Understand and apply systems of wealth. Get empowered with practical strategies for financial growth, business building, and ongoing mentorship with Prophet Malluc.</p>
                    </div>
                  </div>
                  <div className="flex border border-gray-100 overflow-hidden" data-testid="event-schedule-revival">
                    <div className="bg-[#1a2332] text-white px-5 py-5 flex flex-col items-center justify-center min-w-[90px]">
                      <span className="font-heading text-2xl font-medium">6</span>
                      <span className="font-body text-[10px] uppercase tracking-wider text-white/70">PM</span>
                    </div>
                    <div className="px-5 py-4 flex-1">
                      <h4 className="font-heading text-lg font-medium text-gray-900">Revival Night</h4>
                      <p className="text-[13px] text-gray-500 font-body mt-1">A powerful night of healing, deliverance, and the prophetic. Experience the move of God like never before.</p>
                    </div>
                  </div>
                </div>
                <p className="text-[12px] text-gray-400 font-body mt-3 italic">
                  One ticket covers both events for the full day.
                </p>
              </div>
            ) : isMultiDayEvent ? (
              <div>
                <h3 className="font-heading text-xl font-medium text-gray-900 mb-4">Dates</h3>
                <div className="flex border border-gray-100 overflow-hidden">
                  <div className="bg-[#1a2332] text-white px-5 py-5 flex flex-col items-center justify-center min-w-[90px]">
                    <span className="font-heading text-lg font-medium">3</span>
                    <span className="font-body text-[10px] uppercase tracking-wider text-white/70">Days</span>
                  </div>
                  <div className="px-5 py-4 flex-1">
                    <h4 className="font-heading text-lg font-medium text-gray-900">{formatDate(event.start_at)} — {formatDate(event.end_at)}</h4>
                    <p className="text-[13px] text-gray-500 font-body mt-1">Your registration covers all 3 days.</p>
                  </div>
                </div>
              </div>
            ) : null}

            <Separator className="my-8 bg-gray-100" />

            {/* Description */}
            <div>
              <h3 className="font-heading text-xl font-medium text-gray-900 mb-4">About This Event</h3>
              <p className="text-base text-gray-500 font-body leading-7 whitespace-pre-line">
                {event.description}
              </p>
            </div>

            <Separator className="my-8 bg-gray-100" />

            {/* Venue - hide if no real location */}
            {event.location && event.location !== 'To be announced' && (
              <>
                <Separator className="my-8 bg-gray-100" />
                <div>
                  <h3 className="font-heading text-xl font-medium text-gray-900 mb-3">Venue</h3>
                  <div className="flex items-start gap-2 text-base text-gray-500 font-body">
                    <MapPin className="h-5 w-5 mt-0.5 shrink-0" />
                    <span>{event.location}</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right: Ticket panel */}
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-24">
              {isComingSoon ? (
                <div className="border border-gray-100 bg-white p-6 text-center">
                  <h3 className="font-heading text-2xl font-medium text-gray-900">Coming Soon</h3>
                  <p className="text-[13px] text-gray-500 font-body mt-3">Details will be announced. Follow us on social media for updates.</p>
                </div>
              ) : (
              <div className="border border-gray-100 bg-white p-6">
                <div className="flex items-baseline justify-between">
                  <span className="font-heading text-3xl font-medium text-gray-900">
                    {isFreeEvent ? 'Free' : formatPrice(event.price, event.currency)}
                  </span>
                  <span className="text-[13px] text-gray-400 font-body">per seat</span>
                </div>

                {isFreeEvent ? (
                  <div className="mt-4 space-y-3">
                    <p className="text-[13px] text-gray-500 font-body leading-relaxed">
                      This event is free, but seats are limited. Please only register if you are committed to attending. If you cannot make it, do not take a seat — leave it for someone who will be there.
                    </p>
                    <div className="bg-amber-50 border border-amber-100 p-3 rounded-sm">
                      <p className="text-[12px] text-amber-800 font-body font-medium leading-relaxed">
                        After registering, you will be redirected to WhatsApp. You must message us to receive your ticket. No WhatsApp message, no ticket. Please also save the number for any venue changes or updates.
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-[13px] text-gray-500 font-body leading-relaxed mt-4">
                    This is not a charge for the event. This fee protects us against bots and fraudulent reservations.
                  </p>
                )}

                <Separator className="my-5 bg-gray-100" />

                <div className="space-y-3 text-sm font-body">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date</span>
                    <span className="text-gray-700">{formatDate(event.start_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Time</span>
                    <span className="text-gray-700">
                      {isBusinessSummitEvent ? '11:00 AM & 6:00 PM' : isMultiDayEvent ? `${formatDate(event.start_at)} — ${formatDate(event.end_at)}` : formatTime(event.start_at)}
                    </span>
                  </div>
                </div>

                <Separator className="my-5 bg-gray-100" />

                {/* Urgency: tickets selling */}
                <div className="mb-5">
                  <div className="flex justify-between text-sm font-body mb-2">
                    <span className="text-gray-900 font-medium">{event.tickets_sold?.toLocaleString()} seats reserved</span>
                    <span className="text-gray-400">{event.tickets_available?.toLocaleString()} remaining</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#1a2e4a] rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (event.tickets_sold / event.capacity) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-amber-700 font-body font-medium mt-2">
                    Seats are filling fast — reserve yours before they're gone
                  </p>
                </div>

                {!showForm ? (
                  <Button
                    onClick={() => setShowForm(true)}
                    disabled={isSoldOut || isExpired}
                    className="w-full h-12 rounded-sm bg-[#1a2e4a] text-white font-body text-sm font-medium hover:bg-[#0f1b2d] transition-colors tracking-wider uppercase"
                    data-testid="event-detail-buy-ticket-button"
                  >
                    {isSoldOut ? 'Fully Reserved' : isExpired ? 'Event Has Passed' : 'Reserve Your Seat'}
                  </Button>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {isFreeEvent ? (
                      <p className="text-[13px] text-gray-500 font-body leading-relaxed">
                        Please enter your correct details below. Your WhatsApp number is required — you will be redirected to WhatsApp after registering to claim your ticket.
                      </p>
                    ) : isUSEvent ? (
                      <p className="text-[13px] text-gray-500 font-body leading-relaxed">
                        Please enter your correct details below. Your ticket will be sent to your email. Save <span className="font-medium text-gray-700">+1 (862) 300-6513</span> on WhatsApp to receive updates and any changes.
                      </p>
                    ) : (
                      <p className="text-[13px] text-gray-500 font-body leading-relaxed">
                        Please enter your correct details below. Your WhatsApp number is required so we can send you your ticket and notify you of any changes.
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="firstName" className="text-sm font-body font-medium text-gray-700">First Name</Label>
                        <Input
                          id="firstName"
                          value={formData.firstName}
                          onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                          placeholder="First name"
                          required
                          className="mt-1 h-10 rounded-sm border-gray-200 font-body text-sm"
                          data-testid="checkout-attendee-firstname-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-sm font-body font-medium text-gray-700">Last Name</Label>
                        <Input
                          id="lastName"
                          value={formData.lastName}
                          onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                          placeholder="Last name"
                          required
                          className="mt-1 h-10 rounded-sm border-gray-200 font-body text-sm"
                          data-testid="checkout-attendee-lastname-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-body font-medium text-gray-700">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="your@email.com"
                        required
                        className="mt-1 h-10 rounded-sm border-gray-200 font-body text-sm"
                        data-testid="checkout-attendee-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-sm font-body font-medium text-gray-700">
                        {isUSEvent ? 'Phone Number' : 'WhatsApp Number'}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        placeholder={isUSEvent ? '(000) 000-0000' : '+27 00 000 0000'}
                        required
                        className="mt-1 h-10 rounded-sm border-gray-200 font-body text-sm"
                        data-testid="checkout-attendee-phone-input"
                      />
                    </div>
                    <div className="bg-[#f8f6f2] border border-gray-100 p-3 rounded-sm">
                      {isFreeEvent ? (
                        <p className="text-xs text-gray-600 font-body leading-relaxed">
                          After registering, you will be redirected to WhatsApp to receive your ticket. <span className="font-semibold">If you do not message us on WhatsApp, you will not receive a ticket.</span> Please save <span className="font-semibold">+1 (862) 300-6513</span> for venue updates and changes.
                        </p>
                      ) : isUSEvent ? (
                        <p className="text-xs text-gray-600 font-body leading-relaxed">
                          Your ticket will be sent to your email after payment. Please also save <span className="font-semibold">+1 (862) 300-6513</span> on WhatsApp — we will notify you there and by email if there are any changes.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-600 font-body leading-relaxed">
                          After payment, text <span className="font-semibold">+1 (862) 300-6513</span> on WhatsApp to receive your ticket.
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 font-body">
                      Your information is secure and will only be used for event communication.
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full h-12 rounded-sm bg-[#1a2e4a] text-white font-body text-sm font-medium hover:bg-[#0f1b2d] transition-colors tracking-wider uppercase"
                      data-testid="checkout-proceed-to-payment-button"
                    >
                      {submitting ? 'Processing...' : isFreeEvent ? 'Register & Claim Ticket on WhatsApp' : 'Proceed to Payment'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setShowForm(false)}
                      className="w-full text-sm text-gray-400 font-body"
                    >
                      Cancel
                    </Button>
                  </form>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
